import { Flame, HeartPulse, Shield } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { Reveal } from '@/components/ui/Reveal';

interface Org {
  id: 'fire' | 'ems' | 'police';
  icon: LucideIcon;
  title: string;
  iconClass: string;
  gradient: string;
  bullets: string[];
}

const ORGS: Org[] = [
  {
    id: 'fire',
    icon: Flame,
    title: 'Feuerwehr',
    iconClass: 'text-accent-emergency',
    gradient: 'from-red-600/10 via-transparent to-transparent',
    bullets: [
      'Brandbekämpfung mit Wassertanks, Atemschutzgeräten, Hydrauliksets',
      'Drehleitern für Höhenrettung, Feuerwehrkran für Schwerlasthebung',
      'Wachen-Erweiterungen: Werkstatt, Übungsgelände, Großfahrzeughalle',
      '7 Tier-Stufen pro Wache, von Freiwilliger Feuerwehr bis BF-Hauptwache',
    ],
  },
  {
    id: 'ems',
    icon: HeartPulse,
    title: 'Rettungsdienst',
    iconClass: 'text-orange-400',
    gradient: 'from-orange-500/10 via-transparent to-transparent',
    bullets: [
      'Patientenversorgung mit RTW, KTW und NEF',
      'Automatisches Krankenhaus-Routing zum nächsten freien Bett',
      'BOS-Funkstati 1–8 nach realem deutschen Standard',
      'Sprechwunsch-System: Notarzt nachfordern, Verstärkung anfordern',
    ],
  },
  {
    id: 'police',
    icon: Shield,
    title: 'Polizei',
    iconClass: 'text-accent-dispatch',
    gradient: 'from-blue-600/10 via-transparent to-transparent',
    bullets: [
      'Streifendienst, Bereitschaftspolizei, SEK und Bundespolizei',
      'Festnahme-Mechanik mit Wachzellen und Gerichtsüberstellung',
      'Cross-Organization-Einsätze: Tatort sichern, bevor RD ran darf',
      '14 Mission-Typen von Ruhestörung bis Geiselnahme',
    ],
  },
];

export function Organizations() {
  return (
    <section
      id="organizations"
      className="relative px-4 py-24 md:px-6 md:py-32"
    >
      <div className="mx-auto max-w-7xl">
        <Reveal>
          <div className="mb-16 max-w-3xl">
            <p className="mb-3 text-sm uppercase tracking-widest text-accent-emergency">
              Drei Organisationen
            </p>
            <h2 className="font-display text-4xl font-bold leading-tight md:text-5xl">
              Eine Leitstelle. Drei Welten.
            </h2>
            <p className="mt-4 text-lg text-fg-secondary">
              Disponiere Feuerwehr, Rettungsdienst und Polizei mit jeweils eigenen Mechaniken,
              Fahrzeugflotten und Wachen-Erweiterungen.
            </p>
          </div>
        </Reveal>

        <div className="grid gap-4 md:grid-cols-3">
          {ORGS.map((org, i) => (
            <Reveal key={org.id} delay={i * 0.08}>
              <OrgCard org={org} />
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

function OrgCard({ org }: { org: Org }) {
  const Icon = org.icon;
  return (
    <div
      className={[
        'group relative h-full overflow-hidden rounded-xl border border-border-subtle bg-bg-secondary p-8 transition',
        'hover:-translate-y-1 hover:border-border-strong hover:shadow-2xl',
      ].join(' ')}
    >
      {/* Gradient overlay */}
      <div
        className={[
          'pointer-events-none absolute inset-0 bg-gradient-to-br opacity-100',
          org.gradient,
        ].join(' ')}
      />

      <div className="relative">
        <span
          className={[
            'mb-6 inline-flex h-14 w-14 items-center justify-center rounded-xl bg-bg-elevated/80',
            org.iconClass,
          ].join(' ')}
        >
          <Icon className="h-7 w-7" aria-hidden />
        </span>

        <h3 className="mb-4 font-display text-2xl font-bold">{org.title}</h3>

        <ul className="space-y-3">
          {org.bullets.map((b) => (
            <li key={b} className="flex gap-3 text-sm leading-relaxed text-fg-secondary">
              <span
                className={[
                  'mt-1.5 h-1.5 w-1.5 flex-shrink-0 rounded-full',
                  org.iconClass.replace('text-', 'bg-'),
                ].join(' ')}
              />
              <span>{b}</span>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}
